
import React, { useState } from 'react';
import { ArrowLeft, Search, Send, Upload, HelpCircle, MessageSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';

// FAQ data
const faqData = [
  {
    category: "Account",
    questions: [
      {
        id: "account-1",
        question: "How do I change my password?",
        answer: "You can change your password in the Profile & Settings page. Go to the Personal Info tab and find the 'Change Password' section at the bottom of the page."
      },
      {
        id: "account-2",
        question: "How do I update my contact information?",
        answer: "Navigate to Profile & Settings and select the Personal Info tab. Here you can update your phone number and other personal details. Note that email changes require contacting support."
      },
      {
        id: "account-3",
        question: "Can I change my specialty or professional details?",
        answer: "Yes, you can update your specialty, years of experience, and languages spoken in the Professional Info tab of the Profile & Settings page."
      }
    ]
  },
  {
    category: "Patients",
    questions: [
      {
        id: "patients-1",
        question: "Where can I see all my patients?",
        answer: "Access your complete patient list by navigating to the 'My Patients' section in the sidebar menu. Here you can view, search, and filter all your patients."
      },
      {
        id: "patients-2",
        question: "How do I access a patient's case file?",
        answer: "From the 'My Patients' page, click on a patient's name or the 'View' button to access their individual case file with all their medical details and treatment history."
      },
      {
        id: "patients-3",
        question: "How can I message a patient?",
        answer: "You can message patients in two ways: from their case file by clicking the 'Message' button, or through the Messages section in the sidebar menu where you can see all your conversations."
      }
    ]
  },
  {
    category: "Appointments",
    questions: [
      {
        id: "appointments-1",
        question: "How do I set my availability?",
        answer: "Go to the Availability section in the sidebar. Here you can set your working hours for each day of the week and specify which types of appointments (in-clinic, video, voice) you accept during those times."
      },
      {
        id: "appointments-2",
        question: "Can I reschedule an appointment?",
        answer: "Yes, go to the Appointments page, find the appointment you want to reschedule, and click the 'Reschedule' option. You can then select a new date and time based on your availability."
      },
      {
        id: "appointments-3",
        question: "Where can I see upcoming appointments?",
        answer: "Your upcoming appointments are visible on your Dashboard for quick reference, and in the Appointments section for a more detailed view. You can also see them in the Calendar View."
      }
    ]
  },
  {
    category: "Treatments",
    questions: [
      {
        id: "treatments-1",
        question: "How do I create a treatment plan?",
        answer: "To create a treatment plan, first select a patient from your patient list. Then navigate to their case file and click the 'Create Treatment Plan' button. Follow the guided process to define the treatment details."
      },
      {
        id: "treatments-2",
        question: "Where can I view all my treatment plans?",
        answer: "All your created treatment plans are available in the 'Treatment Plans' section. Here you can filter plans by status, patient name, or date range."
      },
      {
        id: "treatments-3",
        question: "How do I update a treatment's progress?",
        answer: "Open the specific treatment plan from the Treatment Plans section, then click on 'Update Progress'. You can mark treatments as completed, add notes, or modify the next steps."
      }
    ]
  },
  {
    category: "Payments",
    questions: [
      {
        id: "payments-1",
        question: "Where can I see my payments?",
        answer: "Your payment information is available in the Finance section under 'My Payments'. This shows all payments processed through the platform, including appointment fees and treatment plans."
      },
      {
        id: "payments-2",
        question: "How and when do I receive payments?",
        answer: "Payments are processed according to your clinic's payment schedule. Typically, they are transferred to your account bi-weekly. Check the 'Payment Schedule' in the Finance section for details specific to your account."
      },
      {
        id: "payments-3",
        question: "How do I request a withdrawal?",
        answer: "In the Finance section, navigate to 'Withdrawal Requests' and click 'New Withdrawal Request'. Enter the amount you wish to withdraw and confirm. Withdrawals are typically processed within 3-5 business days."
      }
    ]
  }
];

const supportCategories = [
  { id: "technical", label: "Technical Support" },
  { id: "account", label: "Account Issues" },
  { id: "billing", label: "Billing & Payments" },
  { id: "feature", label: "Feature Request" },
  { id: "bug", label: "Report a Bug" },
  { id: "other", label: "Other Inquiry" }
];

const FAQSupportPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("faq");
  const [supportCategory, setSupportCategory] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  
  // Filter FAQs based on search query
  const filteredFAQs = searchQuery
    ? faqData.map(category => ({
        ...category,
        questions: category.questions.filter(q => 
          q.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
          q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqData;
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };
  
  // Clear selected file
  const clearFile = () => {
    setSelectedFile(null);
  };
  
  // Handle support form submission
  const handleSupportSubmit = () => {
    if (!supportCategory) {
      toast({
        title: "Category required",
        description: "Please select a support category.",
        variant: "destructive",
      });
      return;
    }
    
    if (!supportMessage.trim()) {
      toast({
        title: "Message required",
        description: "Please enter a message describing your issue or question.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setSupportDialogOpen(false);
      
      // Reset form
      setSupportCategory("");
      setSupportMessage("");
      setSelectedFile(null);
      
      toast({
        title: "Support request sent",
        description: "We'll respond to your inquiry within 24 hours via your registered email.",
      });
    }, 1500);
  };
  
  return (
    <div className="min-h-screen bg-background">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pt-20 pb-24">
        <div className="flex items-center gap-2 mb-6">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="mr-1"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Help & Support</h1>
        </div>
        
        <Tabs defaultValue="faq" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="faq">
              <HelpCircle className="h-4 w-4 mr-2" />
              Frequently Asked Questions
            </TabsTrigger>
            <TabsTrigger value="support">
              <MessageSquare className="h-4 w-4 mr-2" />
              Contact Support
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>
                  Find answers to common questions about using the platform
                </CardDescription>
                
                <div className="relative mt-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search for answers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {filteredFAQs.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-muted-foreground">No results found for "{searchQuery}"</p>
                    <Button 
                      variant="link" 
                      onClick={() => setSearchQuery("")}
                    >
                      Clear search
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {filteredFAQs.map((category) => (
                      <div key={category.category}>
                        <h3 className="font-medium text-lg mb-3">{category.category}</h3>
                        <Accordion type="single" collapsible className="w-full">
                          {category.questions.map((faq) => (
                            <AccordionItem key={faq.id} value={faq.id}>
                              <AccordionTrigger className="text-left">
                                {faq.question}
                              </AccordionTrigger>
                              <AccordionContent>
                                <p className="text-muted-foreground">{faq.answer}</p>
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
              <CardFooter className="border-t pt-6 flex justify-between flex-wrap gap-4">
                <p className="text-sm text-muted-foreground">
                  Didn't find what you're looking for?
                </p>
                <Button onClick={() => setActiveTab("support")}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="support">
            <Card>
              <CardHeader>
                <CardTitle>Contact Support</CardTitle>
                <CardDescription>
                  Get help from our support team - we'll respond within 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="category">Support Category</Label>
                    <Select 
                      value={supportCategory} 
                      onValueChange={setSupportCategory}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {supportCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea 
                      id="message" 
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      placeholder="Describe your issue or question in detail..." 
                      rows={6}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="file">Attach a File (Optional)</Label>
                    <div className="flex items-center justify-between border rounded-md p-2">
                      <div className="flex items-center">
                        <Upload className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {selectedFile ? selectedFile.name : "No file selected"}
                        </span>
                      </div>
                      
                      {selectedFile ? (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={clearFile}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Label htmlFor="file-upload" className="cursor-pointer">
                          <span className="text-sm text-primary">Choose file</span>
                          <input 
                            id="file-upload"
                            type="file" 
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </Label>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Maximum file size: 10MB. Supported formats: JPG, PNG, PDF.
                    </p>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button 
                  onClick={() => setSupportDialogOpen(true)}
                  disabled={!supportMessage.trim()}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit Support Request
                </Button>
              </CardFooter>
            </Card>
            
            {/* Confirmation Dialog */}
            <Dialog open={supportDialogOpen} onOpenChange={setSupportDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit Support Request</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to submit this support request?
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-4">
                  <div className="text-sm">
                    <span className="font-medium">Category:</span>{' '}
                    {supportCategories.find(c => c.id === supportCategory)?.label || "Not selected"}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Message:</span>
                    <p className="mt-1 text-muted-foreground">
                      {supportMessage || "No message provided"}
                    </p>
                  </div>
                  {selectedFile && (
                    <div className="text-sm">
                      <span className="font-medium">Attached File:</span>{' '}
                      {selectedFile.name}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" disabled={isSubmitting}>Cancel</Button>
                  </DialogClose>
                  <Button 
                    onClick={handleSupportSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Confirm Submission"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
      
      <DoctorFooterNav />
    </div>
  );
};

export default FAQSupportPage;
